import {
  aggregateTvAssetTiming,
  isTvAssetResourceTiming,
  isTvAssetUrl,
  toTvAssetResourceTiming,
  type TvAssetResourceTiming,
  type TvAssetTimingMetrics,
  type TvAssetTimingSource,
} from './chart-tv-asset-timing';

export interface TvAssetCollectorHost {
  observeResource(callback: (entries: PerformanceEntry[]) => void): () => void;
  getParentResourceEntries(): PerformanceEntry[];
  getParentTimeOrigin(): number;
}

const createBrowserHost = (): TvAssetCollectorHost => ({
  observeResource(callback) {
    const observer = new PerformanceObserver((list) => {
      callback(list.getEntries());
    });
    observer.observe({ type: 'resource', buffered: true });
    return () => observer.disconnect();
  },
  getParentResourceEntries() {
    return performance.getEntriesByType('resource');
  },
  getParentTimeOrigin() {
    return performance.timeOrigin;
  },
});

const toSourceEntries = (entries: PerformanceEntry[]): TvAssetResourceTiming[] =>
  entries
    .filter(isTvAssetResourceTiming)
    .filter((entry) => isTvAssetUrl(entry.name))
    .map(toTvAssetResourceTiming);

export class TvAssetCollector {
  readonly #host: TvAssetCollectorHost;
  readonly #parentByName = new Map<string, TvAssetResourceTiming>();

  #disconnect: (() => void) | undefined;
  #parentReadable = false;
  #started = false;

  constructor(host: TvAssetCollectorHost = createBrowserHost()) {
    this.#host = host;
  }

  start() {
    if (this.#started) return;
    this.#started = true;

    try {
      this.#ingestParent(this.#host.getParentResourceEntries());
      this.#disconnect = this.#host.observeResource((entries) => this.#ingestParent(entries));
      this.#parentReadable = true;
    } catch {
      this.#parentReadable = this.#parentByName.size > 0;
    }
  }

  stop({
    windowStart,
    windowEnd,
    iframe,
  }: {
    windowStart: number;
    windowEnd: number;
    iframe?: TvAssetTimingSource | 'unavailable';
  }): TvAssetTimingMetrics {
    try {
      this.#disconnect?.();
    } catch {
      // Observability must never interrupt the chart lifecycle.
    }
    this.#disconnect = undefined;

    try {
      this.#ingestParent(this.#host.getParentResourceEntries());
    } catch {
      // Keep whatever the observer already captured.
    }

    let parentTimeOrigin = 0;
    try {
      parentTimeOrigin = this.#host.getParentTimeOrigin();
    } catch {
      parentTimeOrigin = 0;
    }

    const parent: TvAssetTimingSource | undefined = this.#parentReadable
      ? { entries: [...this.#parentByName.values()], timeOrigin: parentTimeOrigin }
      : undefined;

    return aggregateTvAssetTiming({
      parent,
      iframe,
      window: { startTime: windowStart, endTime: windowEnd },
      parentTimeOrigin,
    });
  }

  #ingestParent(entries: PerformanceEntry[]) {
    for (const entry of toSourceEntries(entries)) {
      this.#parentByName.set(entry.name, entry);
    }
  }
}

export const readIframeTvAssetSource = (
  getInnerWindow: () => { performance?: Performance } | null | undefined,
): TvAssetTimingSource | 'unavailable' => {
  try {
    const inner = getInnerWindow();
    const innerPerformance = inner?.performance;
    if (!innerPerformance) return 'unavailable';
    return {
      entries: toSourceEntries(innerPerformance.getEntriesByType('resource')),
      timeOrigin: innerPerformance.timeOrigin,
    };
  } catch {
    return 'unavailable';
  }
};
